import React, {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react';

/** HTMLElement向けの構造的Ref（RefObject/MutableRefObjectの代替） */
type ElRef<T extends Element = HTMLElement> = { current: T | null };

type Props = {
  /** 横スクロールしている表のコンテナ(div)への参照 */
  targetRef: ElRef<HTMLElement>;
  className?: string;
  style?: CSSProperties;
  outerRef?: ElRef<HTMLDivElement>; // テスト用
};

/**
 * 表の scrollWidth/scrollLeft に追従しつつ、
 * 表コンテナとヘッダーの左右オフセット差も同期させてズレを防ぐ。
 * ヘッダー側は overflow: hidden（スクロールバー非表示）。
 */
export default function SyncedScrollHeader({
  targetRef,
  className,
  style,
  children,
  outerRef,
}: PropsWithChildren<Props>) {
  const innerRef = useRef<HTMLDivElement>(null); // 幅と左右パディングを調整するラッパ

  // ...省略...
  useEffect(() => {
    const outer = outerRef?.current;
    const inner = innerRef.current;
    const target = targetRef.current;
    if (!outer || !inner || !target) return;

    const refreshGeometry = () => {
      const t = target as HTMLElement;

      // テーブルの左右パディングを取得
      const cs = getComputedStyle(t);
      const padL = parseFloat(cs.paddingLeft || '0');
      const padR = parseFloat(cs.paddingRight || '0');

      // はみ出し時=scrollWidth、収まる時=clientWidth（※どちらもpadding込み）
      const contentWidth = Math.max(t.scrollWidth, t.clientWidth);

      // ヘッダーの“コンテンツ幅”は padding を引いた値にする
      const innerWidth = Math.max(0, contentWidth - padL - padR);
      inner.style.width = `${innerWidth}px`;

      // 左右 padding をテーブルと合わせる（右端のズレ対策）
      inner.style.paddingLeft = `${padL}px`;
      inner.style.paddingRight = `${padR}px`;

      // ヘッダーの左位置は「テーブルのボックス左端」に合わせる
      const outerRect = outer.getBoundingClientRect();
      const targetRect = t.getBoundingClientRect();
      const shiftLeft = Math.max(0, targetRect.left - outerRect.left);

      inner.style.marginLeft = `${shiftLeft}px`;
      inner.style.marginRight = '0';

      // 初期スクロール位置を同期
      outer.scrollLeft = t.scrollLeft;
    };

    refreshGeometry();

    const ro = new ResizeObserver(refreshGeometry);
    ro.observe(target as Element);

    window.addEventListener('resize', refreshGeometry);

    const syncTableToHeader = () => {
      outer.scrollLeft = (target as HTMLElement).scrollLeft;
    };
    (target as HTMLElement).addEventListener('scroll', syncTableToHeader, {
      passive: true,
    });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', refreshGeometry);
      (target as HTMLElement).removeEventListener('scroll', syncTableToHeader);
    };
  }, [targetRef]);

  return (
    <div
      ref={outerRef}
      className={`mt-4 mb-2 ${className}`}
      style={{ width: '100%', overflow: 'hidden', ...style }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
